import { Check, ChevronDown, GraduationCap } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import { masterclassesPath } from "@/routes"
import type {
  MasterclassCompletionOp,
  PublishedMasterclassPickerItem,
} from "@/types/crew"

/**
 * OKS-58 — Masterclass-completion filter rail field.
 *
 * Drops onto the Crew Roster filter rail (`crew-filter-rail.tsx`)
 * between Joined and At-risk. Renders a Popover-anchored:
 *   - operator `RadioGroup` (Has completed / Has not completed)
 *   - masterclass `Command` Combobox picker (alpha-sorted by title;
 *     each item shows title + muted topic on a second line)
 *   - Apply / Clear button row
 *
 * **Both-or-neither pair invariant** — the field stages an operator +
 * a masterclass id locally and commits BOTH on Apply via
 * `onChange(op, id)`. Clear emits `onChange(null, null)`. The parent
 * (`CrewIndex`) wires Apply to `useCrewListUrl.push({ ... })` which
 * mirrors the pair to the URL (`buildCrewListParams` emits both or
 * neither — locked Vitest canary).
 *
 * **5 states** (UX Spec §3):
 *   - **Loading** — Popover trigger never blocks roster fetch; if the
 *     brand list hydrates async, the picker shows a skeleton row × 3.
 *     V1 ships the list with the page payload, so this state is rare
 *     in practice (the trigger button itself is never disabled on
 *     loading).
 *   - **Empty** — brand has zero published masterclasses → Popover
 *     content swaps to a centered `GraduationCap` glyph + heading +
 *     body + secondary CTA `Author one →` → `masterclassesPath()`.
 *     Apply disabled.
 *   - **Error** — V1 ships the list synchronously on the page payload,
 *     so the only error path is `router.reload` failure. Handled
 *     globally on the page; not surfaced inside the Popover.
 *   - **Success** — operator + picker both interactive; Apply enabled
 *     when an id is selected.
 *   - **Edge** — long title truncates in the trigger badge (24ch +
 *     Tooltip); search returns no match → `CommandEmpty` reads "No
 *     masterclass matches.".
 *
 * **Wire-format invariant** — operator + id both committed on Apply.
 * The active-rule trigger badge derives its title from the picker
 * `options` lookup; if the id isn't in the list (stale unpublish
 * mid-session), the badge falls back to the operator-only label.
 *
 * **Locked testids (UX Spec §2 + LQE Playwright contract):**
 *   - `crew-list-filter-masterclass-completion`        — trigger Button
 *   - `crew-list-filter-masterclass-completion-popover` — Popover content
 *   - `crew-list-filter-masterclass-completion-op`     — operator RadioGroup
 *   - `crew-list-filter-masterclass-completion-op-is-completed`
 *   - `crew-list-filter-masterclass-completion-op-is-not-completed`
 *   - `crew-list-filter-masterclass-completion-picker` — Combobox container
 *   - `crew-list-filter-masterclass-completion-apply`  — Apply Button
 *   - `crew-list-filter-masterclass-completion-clear`  — Clear Button
 *   - `crew-list-filter-masterclass-completion-empty`  — empty-state card
 */
export interface MasterclassCompletionFieldProps {
  op: MasterclassCompletionOp | null
  masterclassId: number | null
  options: PublishedMasterclassPickerItem[]
  onChange: (
    op: MasterclassCompletionOp | null,
    masterclassId: number | null,
  ) => void
}

export function MasterclassCompletionField({
  op,
  masterclassId,
  options,
  onChange,
}: MasterclassCompletionFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Staged state — Apply commits to URL via onChange; Cancel/Esc
  // discards. Defaults to `is_completed` per UX Spec §2 (Open Q2 —
  // "the more common nudge"). On Popover open transition we
  // re-initialize from the current props so out-of-band URL changes
  // (chip remove, segment apply) reset the form correctly without
  // touching local state mid-render.
  const [stagedOp, setStagedOp] = useState<MasterclassCompletionOp>(
    op ?? "is_completed",
  )
  const [stagedId, setStagedId] = useState<number | null>(masterclassId)

  // Re-init staged state on the Popover OPEN transition. We avoid
  // `useEffect` (eslint react-hooks/set-state-in-effect) by intercepting
  // the `onOpenChange` callback — the only path that flips `open` →
  // true is a user-triggered click on the trigger.
  function handleOpenChange(next: boolean) {
    if (next && !open) {
      setStagedOp(op ?? "is_completed")
      setStagedId(masterclassId)
    }
    setOpen(next)
  }

  const isActive = op !== null && masterclassId !== null
  const isEmpty = options.length === 0

  // Active-rule summary on the trigger Badge. Title resolved from the
  // picker list lookup; falls back to a generic operator-only label
  // when the id isn't in the list (rare: stale unpublish mid-session
  // OR a saved-segment rule whose target was unpublished). The chip
  // surface on the chip rail handles the `(Unpublished)` suffix
  // explicitly — the trigger Badge stays terse.
  const triggerSummary = (() => {
    if (!isActive) return null
    const item = options.find((m) => m.id === masterclassId)
    const opLabel = t(
      `crew_segments.filter.masterclass_completion.operator.${op}` as const,
    )
    return item ? `${opLabel} · ${item.title}` : opLabel
  })()

  function handleApply() {
    if (stagedId === null) return
    onChange(stagedOp, stagedId)
    setOpen(false)
  }

  function handleClear() {
    onChange(null, null)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="crew-list-filter-masterclass-completion"
            aria-label={t(
              "crew_segments.filter.masterclass_completion.trigger_aria",
            )}
            aria-expanded={open}
          >
            {t("crew_segments.filter.masterclass_completion.trigger_label")}
            {triggerSummary && (
              <Badge
                variant="secondary"
                className="ml-2 max-w-[14rem] truncate"
                title={triggerSummary}
              >
                {triggerSummary}
              </Badge>
            )}
            <ChevronDown className="ml-1 size-3" aria-hidden="true" />
          </Button>
        }
      />
      <PopoverContent
        data-testid="crew-list-filter-masterclass-completion-popover"
        className="w-80"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium">
            {t("crew_segments.filter.masterclass_completion.popover_title")}
          </p>

          {/* Operator switch — always rendered; disabled on empty state.
              `aria-labelledby` ties to the visually-hidden legend
              ("Operator") for a11y. */}
          <fieldset className="flex flex-col gap-2">
            <Label className="sr-only" id="mc-completion-op-legend">
              {t("crew_segments.filter.masterclass_completion.operator_legend")}
            </Label>
            <RadioGroup
              value={stagedOp}
              onValueChange={(value) =>
                setStagedOp(value as MasterclassCompletionOp)
              }
              data-testid="crew-list-filter-masterclass-completion-op"
              aria-labelledby="mc-completion-op-legend"
              className="flex flex-col gap-2"
              disabled={isEmpty}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="is_completed"
                  id="mc-completion-op-is-completed"
                  data-testid="crew-list-filter-masterclass-completion-op-is-completed"
                />
                <Label
                  htmlFor="mc-completion-op-is-completed"
                  className="cursor-pointer text-sm font-normal"
                >
                  {t(
                    "crew_segments.filter.masterclass_completion.operator.is_completed",
                  )}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="is_not_completed"
                  id="mc-completion-op-is-not-completed"
                  data-testid="crew-list-filter-masterclass-completion-op-is-not-completed"
                />
                <Label
                  htmlFor="mc-completion-op-is-not-completed"
                  className="cursor-pointer text-sm font-normal"
                >
                  {t(
                    "crew_segments.filter.masterclass_completion.operator.is_not_completed",
                  )}
                </Label>
              </div>
            </RadioGroup>
          </fieldset>

          {/* Picker — Command Combobox on success; empty-state card on
              no-published-mcs. The Combobox's internal `CommandEmpty`
              reads "No masterclass matches." for in-list search misses
              (distinct from the outer empty-state below). */}
          {isEmpty ? (
            <EmptyPickerCard />
          ) : (
            <Picker
              options={options}
              selectedId={stagedId}
              onSelect={(id) => setStagedId(id)}
            />
          )}

          <div className="flex items-center justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="crew-list-filter-masterclass-completion-clear"
              onClick={handleClear}
              disabled={!isActive}
            >
              {t("crew_segments.filter.masterclass_completion.clear")}
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid="crew-list-filter-masterclass-completion-apply"
              onClick={handleApply}
              disabled={isEmpty || stagedId === null}
            >
              {t("crew_segments.filter.masterclass_completion.apply")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Picker — Command Combobox over `options`. Selecting an item flips
 * `selectedId`; clicking the same row again is a no-op (NOT a
 * deselect — Clear in the footer handles deselect).
 */
function Picker({
  options,
  selectedId,
  onSelect,
}: {
  options: PublishedMasterclassPickerItem[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  const { t } = useTranslation()
  return (
    <Command data-testid="crew-list-filter-masterclass-completion-picker">
      <CommandInput
        placeholder={t(
          "crew_segments.filter.masterclass_completion.picker.search_placeholder",
        )}
      />
      <CommandList className="max-h-60">
        <CommandEmpty>
          {t("crew_segments.filter.masterclass_completion.picker.search_empty")}
        </CommandEmpty>
        <CommandGroup>
          {options.map((option) => {
            const isSelected = option.id === selectedId
            return (
              <CommandItem
                key={option.id}
                // cmdk filters on the `value` string — include the
                // title to make type-to-filter work; the integer id is
                // surfaced separately via `onSelect`.
                value={`${option.title} ${option.topic}`}
                onSelect={() => onSelect(option.id)}
                className="cursor-pointer"
                data-testid={`crew-list-filter-masterclass-completion-picker-item-${option.id}`}
              >
                <Check
                  className={cn(
                    "mr-2 size-4",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                  aria-hidden="true"
                />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{option.title}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {option.topic}
                  </span>
                </div>
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

/**
 * Empty-state Popover variant — rendered when the brand has zero
 * published masterclasses. Replaces the Combobox with a centered
 * `GraduationCap` glyph + heading + body + secondary CTA pointing to
 * the brand-side masterclass index. Apply stays disabled at the
 * field-level Apply button; operator radios disable above.
 */
function EmptyPickerCard() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="crew-list-filter-masterclass-completion-empty"
      className="border-border bg-muted/30 flex flex-col items-center gap-2 rounded-md border p-4 text-center"
    >
      <GraduationCap
        className="text-muted-foreground size-6"
        aria-hidden="true"
      />
      <h3 className="text-sm font-medium">
        {t("crew_segments.filter.masterclass_completion.picker.empty_title")}
      </h3>
      <p className="text-muted-foreground text-xs">
        {t("crew_segments.filter.masterclass_completion.picker.empty_body")}
      </p>
      <a
        className="text-primary text-xs underline-offset-2 hover:underline"
        href={masterclassesPath()}
      >
        {t("crew_segments.filter.masterclass_completion.picker.empty_cta")}
      </a>
    </div>
  )
}
